import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import PrivacyPolicy from './screens/PrivacyPolicy.jsx';
import LegalScreen from './legal/LegalScreen.jsx';
import PropertyScreen from './screens/PropertyScreen.jsx';
import ZoneScreen from './screens/ZoneScreen.jsx';
import HeatingStatusScreen from './screens/HeatingStatusScreen.jsx';
import ScheduleScreen from './screens/ScheduleScreen.jsx';
import PlusHoursScreen from './screens/PlusHoursScreen.jsx';
import HolidayScreen from './screens/HolidayScreen.jsx';
import DataScreen from './screens/DataScreen.jsx';
import AlarmDetailScreen from './screens/AlarmDetailScreen.jsx';
import HelpScreen from './screens/HelpScreen.jsx';
import VideosScreen from './help/VideosScreen.jsx';
import FaqLinksScreen from './help/FaqLinksScreen.jsx';
import WatchHelpListScreen from './help/WatchHelpListScreen.jsx';
import WatchHelpTopicScreen from './help/WatchHelpTopicScreen.jsx';
import DsrFaqListScreen from './help/DsrFaqListScreen.jsx';
import DsrFaqScreen from './help/DsrFaqScreen.jsx';
import FlagGate from './flags/FlagGate.jsx';
import GatedLayout from './navigation/GatedLayout.jsx';
import ZoneRoute from './navigation/ZoneRoute.jsx';
import HomeRoute from './navigation/HomeRoute.jsx';
import PropertyLanding from './navigation/PropertyLanding.jsx';

// App-style navigation for the SPA. Two pages — Property
// (`/property/:homeId`, with `/` resolving which home to land on) and Zone
// (`/zone/:homeId/:prtId/:endpointId`) — plus a dedicated screen per action, each a
// real route rather than a modal. Every page names its home in the URL, so a tab's
// context is its address: nothing about "which home" is shared between tabs. The
// one account-level page, Help (`/help`, opened from the Property screen's menu),
// names no home because it does not depend on one.
//
// Exported without the surrounding router so tests can mount it inside a
// MemoryRouter and drive specific routes.
export function AppRoutes() {
  return (
    <Routes>
      {/* Public — registered OUTSIDE the auth gate so they stay reachable when
          signed out. /privacy-policy redirects to the hosted privacy notice;
          /legal lists both hosted documents, and is public both because a legal
          page should be linkable without an account and because the stale-terms
          notice links here from a state where the gate is showing that notice
          instead of any screen. The /password-code reset bridge is a separate
          static page, not part of this router, so it is unaffected either way. */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/legal" element={<LegalScreen />} />

      {/* Everything below is behind the auth gate. GatedLayout renders the
          signed-in screens (its <Outlet/>) only when authenticated, wrapped in the
          untrusted-data ErrorBoundary + the SessionGate. */}
      <Route element={<GatedLayout />}>
        {/* Bare entry: pick the remembered / default home and redirect to its page. */}
        <Route path="/" element={<PropertyLanding />} />

        {/* The Help section (the app's HelpMenu), reached from the Property
            screen's menu drawer. Account-level, so none of it names a home. */}
        <Route path="/help" element={<HelpScreen />} />
        <Route path="/help/videos" element={<VideosScreen />} />
        <Route path="/help/faqs" element={<FaqLinksScreen />} />
        {/* Pilot-only sections, as in the app's Help menu. The gate is on the
            routes, not just the menu entries, since a web page has an address. */}
        <Route element={<FlagGate flag="pilot" />}>
          <Route path="/help/watch" element={<WatchHelpListScreen />} />
          <Route path="/help/watch/:topic" element={<WatchHelpTopicScreen />} />
          <Route path="/help/dsr" element={<DsrFaqListScreen />} />
          <Route path="/help/dsr/:index" element={<DsrFaqScreen />} />
        </Route>

        {/* A home's Property page (index) and its property-level actions. HomeRoute
            validates the home against the seeded account before any of them render. */}
        <Route path="/property/:homeId" element={<HomeRoute />}>
          <Route index element={<PropertyScreen />} />
          <Route path="holiday" element={<HolidayScreen />} />
          <Route path="data" element={<DataScreen />} />
          <Route path="data/alarm/:alarmId" element={<AlarmDetailScreen />} />
        </Route>

        {/* Zone detail + its nested action screens. ZoneRoute validates the
            [homeId, prtId, endpointId] against the seeded account once and provides
            the typed zone tuple to the screen + actions. */}
        <Route path="/zone/:homeId/:prtId/:endpointId" element={<ZoneRoute />}>
          <Route index element={<ZoneScreen />} />
          <Route path="status" element={<HeatingStatusScreen />} />
          <Route path="schedule" element={<ScheduleScreen />} />
          <Route path="boost" element={<PlusHoursScreen />} />
        </Route>

        {/* Unknown private paths → Property, so a mistyped/legacy URL never renders
            a blank page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
